import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { SkillService } from '../../services/skill.service';
import { SkillPersistenceService } from '../../services/skill-persistence.service';
import { SkillDetail } from '../../models/skill-detail.model';
import { SkillConstants } from '../../constants/skill-constants';
import { PollCompletionService, PollResult } from '../../../core/services/poll-completion.service';
import { AssignmentGroupInteractionService } from '../../../system-admin/assignment-group/services/assignment-group-interaction.service';
import { AuditTrialDetails } from '../../../shared/models/audits/audit-trial-details';

@Component({
  selector: 'app-skill-details',
  templateUrl: './skill-details.component.html',
  styleUrls: ['./skill-details.component.scss']
})
export class SkillDetailsComponent implements OnInit, OnDestroy {
  code: string = '';
  skill: SkillDetail | null = null;
  isLoading: boolean = true;
  errorMessage: string | null = null;
  activeIndex: number = 0;
  accessToSkillDetails: boolean = false;
  isTrainingItemSliderOpen: boolean = false;
  isSkillSliderOpen: boolean = false;
  isTrainingItemsTabActive: boolean = false; // Lazy load flag for training items tab
  isAuditTabActive: boolean = false; // Lazy load flag for audit tab
  auditTrailData: AuditTrialDetails[] = [];
  isAuditDataLoading: boolean = false;
  auditLoadErrorMessage: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private skillService: SkillService,
    private skillPersistenceService: SkillPersistenceService,
    private messageService: MessageService,
    private pollCompletionService: PollCompletionService,
    private assignmentGroupInteractionService: AssignmentGroupInteractionService
  ) {
    // Subscribe to poll completion events (following document-details pattern)
    this.pollCompletionService.completion$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: PollResult) => {
          this.handlePollCompletion(result);
        }
      });

    this.skillPersistenceService.closeAndResetTrainingItemSlider$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isClose) => {
        if (isClose) {
          this.isTrainingItemSliderOpen = false;
        }
      });

    this.skillPersistenceService.closeAndResetSkillSlider$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isClose) => {
        if (isClose) {
          this.isSkillSliderOpen = false;
          this.loadSkillDetails();
        }
      });
  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.code = params['skillId'];
        if (this.code) {
          this.loadSkillDetails();
        } else {
          this.errorMessage = 'Skill ID not found in route';
          this.accessToSkillDetails = false;
          this.isLoading = false;
        }
      });
  }

  /**
   * Load skill details from the service
   */
  loadSkillDetails(): void {
    this.isLoading = true;
    this.errorMessage = null;
    
    this.skillService.getSkillByCode(this.code)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: SkillDetail) => {
          this.skill = data;
          this.accessToSkillDetails = true;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading skill details:', error);
          this.skill = null;
          if (error.status === 403) {
            this.errorMessage = error.error?.detail || SkillConstants.SKILL_ACCESS_DENIED_ERROR;
          } else {
            this.errorMessage = '';
          }
          this.accessToSkillDetails = false;
          this.isLoading = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle tab change event for lazy loading training items
   */
  onTabChange(event: any): void {
    this.activeIndex = event.index;

    // Lazy load training items tab (index 1)
    if (event.index === 1) {
      this.isTrainingItemsTabActive = true;
    }

    // Lazy load audit tab (index 2)
    if (event.index === 2) {
      this.isAuditTabActive = true;
      this.loadAuditTrail();
    }
  }

  /**
   * Fetch the audit trail for the current skill (lazy-loaded when the
   * Audit tab is opened the first time, then refreshed on subsequent opens
   * so newly published events are visible without reloading the page).
   */
  loadAuditTrail(): void {
    if (!this.skill) {
      return;
    }
    this.isAuditDataLoading = true;
    this.auditLoadErrorMessage = null;
    this.skillService.getSkillAudit(this.skill.code)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: AuditTrialDetails[]) => {
          this.auditTrailData = data || [];
          this.isAuditDataLoading = false;
        },
        error: (error) => {
          console.error('Error loading skill audit trail:', error);
          this.auditTrailData = [];
          this.auditLoadErrorMessage = 'Failed to load audit trail.';
          this.isAuditDataLoading = false;
        }
      });
  }

  /**
   * Handles poll completion events from the polling service
   */
  private handlePollCompletion(result: PollResult): void {
    if (result.domain !== 'LinkTrainingItems') {
      return;
    }
    if (result.status !== 'completed') {
      const operationName = 'linking training items';
      this.pollCompletionService.displayPollError(result, operationName);
      return;
    }
    this.handleLinkTrainingItemsCompletion();
  }

  private handleLinkTrainingItemsCompletion(): void {
    this.messageService.add({
      severity: SkillConstants.Severity_Success,
      summary: SkillConstants.LinkedTrainingItems_Successfully_Updated_Message,
      sticky: true
    });
    this.assignmentGroupInteractionService.updatePanelListOnCreateOrEdit();
    this.loadSkillDetails();
  }

  /**
   * Get the appropriate error message to display
   */
  getSkillDetailsErrorMessage(): string {
    return this.errorMessage || SkillConstants.SKILL_LOAD_ERROR;
  }

  openAddTrainingItems(){
    this.isTrainingItemSliderOpen = !this.isTrainingItemSliderOpen;
  }

  editSkill(): void {
    if (!this.isEditSkillEnabled()) {
      return;
    }

    this.isSkillSliderOpen = true;
  }

  closeSkillSlider(isSliderActive: boolean): void {
    this.isSkillSliderOpen = isSliderActive;
    if (!isSliderActive) {
      this.loadSkillDetails();
    }
  }

  isEditSkillEnabled(): boolean {
    return !!this.skill?.skillAuthorizationFlag?.isEditSkillEnabled;
  }

  closeTrainingItemSlider(isSliderActive : boolean){
    this.isTrainingItemSliderOpen = isSliderActive;
  }
}
